import { useTranslation } from 'react-i18next';
import { formatPrice } from '@/components/ui/Price';
import type { QuoteResponse } from './deliveryApi';
import { quoteCityLabel } from './servedCities';

export type QuoteStatus =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'ok';
      distance_km?: string;
      duration_min?: number;
      city?: string;
      sector?: string;
      fee_ttc: string;
      min_order_ttc?: string;
    }
  | { status: 'out_of_range'; distance_km: string; max_distance_km: string }
  | { status: 'city_not_served'; city?: string }
  | { status: 'disabled' }
  | { status: 'error'; code: 'GEOCODE_FAILED' | 'ROUTES_API_ERROR' };

interface Props {
  quote: QuoteStatus;
  onRetry?: () => void;
}

export function quoteFromResponse(
  data: QuoteResponse | undefined,
  isLoading: boolean,
  isError: boolean,
): QuoteStatus {
  if (isLoading) return { status: 'loading' };
  if (isError) return { status: 'error', code: 'ROUTES_API_ERROR' };
  if (!data) return { status: 'idle' };
  if (data.ok) {
    return {
      status: 'ok',
      distance_km: data.distance_km,
      duration_min: data.duration_min,
      city: data.city,
      sector: data.sector,
      fee_ttc: data.fee_ttc,
      min_order_ttc: data.min_order_ttc,
    };
  }
  if (data.reason === 'OUT_OF_RANGE') {
    return {
      status: 'out_of_range',
      distance_km: data.distance_km ?? '0',
      max_distance_km: data.max_distance_km ?? '0',
    };
  }
  if (data.reason === 'CITY_NOT_SERVED') {
    // SC-1426 — un secteur désactivé : c'est lui qui n'est pas livré, sa commune peut l'être.
    return { status: 'city_not_served', city: quoteCityLabel(data.city, data.sector) };
  }
  if (data.reason === 'GEOCODE_FAILED' || data.reason === 'ROUTES_API_ERROR') {
    return { status: 'error', code: data.reason };
  }
  // SC-574: DELIVERY_DISABLED used to land on 'idle', i.e. the panel rendered
  // nothing at all — the customer saw a complete address, no explanation, and a
  // Continue button that refused with "delivery fee unconfirmed". Say it.
  if (data.reason === 'DELIVERY_DISABLED') {
    return { status: 'disabled' };
  }
  return { status: 'idle' };
}

// Virgule décimale, comme tous les montants de la page (formatPrice) : le bloc
// ne mélange pas « 3.20 km » et « 2,00 € ».
function formatKm(km: string): string {
  return km.replace('.', ',');
}

const BOX = 'border-2 border-solid p-3 font-mono text-sm';
const BOX_INFO = `${BOX} border-ink bg-paper`;
const BOX_ALERT = `${BOX} border-sale-red bg-cream`;

export function DeliveryQuotePanel({ quote, onRetry }: Props) {
  const { t } = useTranslation();
  const line = (label: string, value: string | number) =>
    t('checkout.delivery.labelValue', { label, value });

  if (quote.status === 'idle') return null;

  if (quote.status === 'loading') {
    return (
      <div data-testid="quote-loading" className={BOX_INFO}>
        <span>{t('checkout.delivery.computing')}</span>
      </div>
    );
  }

  if (quote.status === 'ok') {
    return (
      <div data-testid="quote-ok" className={BOX_INFO}>
        {quote.city && (
          <div>
            📍 {line(t('checkout.delivery.city'), quoteCityLabel(quote.city, quote.sector) ?? quote.city)}
          </div>
        )}
        {quote.distance_km !== undefined && (
          <div>
            🚗 {line(t('checkout.delivery.distance'), `${formatKm(quote.distance_km)} km`)}
          </div>
        )}
        {quote.duration_min !== undefined && (
          <div>
            ⏱ {line(t('checkout.delivery.duration'), `${quote.duration_min} min`)}
          </div>
        )}
        <div>
          💶 {line(t('checkout.delivery.fee'), formatPrice(Math.round(parseFloat(quote.fee_ttc) * 100)))}
        </div>
      </div>
    );
  }

  if (quote.status === 'out_of_range') {
    return (
      <div data-testid="quote-out-of-range" className={BOX_ALERT}>
        <strong>⚠ {t('checkout.delivery.outOfRange.title')}</strong>
        <p>
          {t('checkout.delivery.outOfRange.body', {
            distance: formatKm(quote.distance_km),
            max: formatKm(quote.max_distance_km),
          })}
        </p>
        <p>{t('checkout.delivery.outOfRange.altSuggestion')}</p>
      </div>
    );
  }

  if (quote.status === 'city_not_served') {
    return (
      <div data-testid="quote-city-not-served" className={BOX_ALERT}>
        <strong>⚠ {t('checkout.delivery.cityNotServed.title')}</strong>
        <p>
          {quote.city
            ? t('checkout.delivery.cityNotServed.body', { city: quote.city })
            : t('checkout.delivery.cityNotServed.bodyUnknown')}
        </p>
        <p>{t('checkout.delivery.outOfRange.altSuggestion')}</p>
        {onRetry && (
          <button type="button" onClick={onRetry} data-testid="quote-retry" className="mt-2 min-h-[44px] border-2 border-solid border-ink bg-paper px-3 py-1">
            {t('checkout.delivery.serviceError.retry')}
          </button>
        )}
      </div>
    );
  }

  if (quote.status === 'disabled') {
    return (
      <div data-testid="quote-disabled" className={BOX_ALERT}>
        <strong>⚠ {t('checkout.delivery.disabled.title')}</strong>
        <p>{t('checkout.delivery.disabled.body')}</p>
      </div>
    );
  }

  return (
    <div data-testid="quote-error" className={`${BOX} border-ink bg-cream`}>
      <strong>{t('checkout.delivery.serviceError.title')}</strong>
      <p>{t('checkout.delivery.serviceError.body')}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} data-testid="quote-retry" className="mt-2 min-h-[44px] border-2 border-solid border-ink bg-paper px-3 py-1">
          {t('checkout.delivery.serviceError.retry')}
        </button>
      )}
    </div>
  );
}
